import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Collapse, Input, InputNumber, Segmented, Spin, Switch, Tooltip, Typography, message } from 'antd';
import { RedoOutlined, BgColorsOutlined, UploadOutlined } from '@ant-design/icons';
import { ingestionApi } from '../ingestionApi';
import { catalogImageUrl } from '../../images';

const { Text } = Typography;

const thumb = (on) => ({
    width: 72,
    height: 72,
    objectFit: 'cover',
    borderRadius: 4,
    cursor: 'pointer',
    border: on ? '3px solid #1677ff' : '3px solid transparent',
    opacity: on ? 1 : 0.7,
});

/** Selectable thumbnail with an optional corner badge (e.g. "cutout"). */
const PickThumb = ({ url, on, onClick, badge, testId }) => (
    <div style={{ position: 'relative', lineHeight: 0 }}>
        <img src={url} alt="" onClick={onClick} style={thumb(on)} />
        {badge && (
            <span
                style={{
                    position: 'absolute',
                    top: 3,
                    left: 3,
                    background: '#1677ff',
                    color: '#fff',
                    fontSize: 9,
                    lineHeight: '13px',
                    padding: '0 4px',
                    borderRadius: 3,
                }}
            >
                {badge}
            </span>
        )}
    </div>
);

// PBR is a tri-state: unset (`auto`) lets the pipeline decide per variant from
// its materials/tags or photo; on/off is an explicit override.
export const pbrModeOf = (enablePbr) => (enablePbr === true ? 'on' : enablePbr === false ? 'off' : 'auto');
export const enablePbrOf = (mode) => (mode === 'on' ? true : mode === 'off' ? false : undefined);

export const PbrModeControl = ({ value, onChange, autoPbr }) => (
    <span>
        <Text>PBR maps </Text>
        <Segmented
            size="small"
            value={value}
            onChange={onChange}
            options={[
                { value: 'auto', label: 'Auto' },
                { value: 'on', label: 'On' },
                { value: 'off', label: 'Off' },
            ]}
        />
        {value === 'auto' && (
            <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>
                {autoPbr
                    ? `last decided: ${autoPbr.enabled ? 'on' : 'off'} (${autoPbr.reason})`
                    : 'on only for glass, metal, gloss and similar reflective items'}
            </Text>
        )}
    </span>
);

export const PolycountInput = ({ value, onChange }) => (
    <span>
        <Text>Target polycount </Text>
        <InputNumber
            size="small"
            min={100}
            max={300000}
            step={1000}
            value={value}
            onChange={onChange}
            placeholder="default"
            style={{ width: 120 }}
        />
    </span>
);

/** Shared texture settings — identical for model generation and retexturing. */
const SettingsToggles = ({ pbr, setPbr, autoPbr, removeLighting, setRemoveLighting, polycount, setPolycount }) => (
    <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'center' }}>
        <PbrModeControl value={pbr} onChange={setPbr} autoPbr={autoPbr} />
        <span>
            <Switch checked={removeLighting} onChange={setRemoveLighting} />{' '}
            <Text>Remove baked lighting</Text>
        </span>
        {setPolycount && <PolycountInput value={polycount} onChange={setPolycount} />}
    </div>
);

/**
 * Per-variant meshy config for model items, as two collapsible panels that share
 * the same texture settings (PBR + remove-lighting):
 *  - Regenerate model: settings + target polycount + optional multi-image selection.
 *  - Retexture: settings + style image → re-skins the existing geometry.
 */
export const MeshyGenConfig = ({
    itemId,
    variant,
    onModelChange,
    onRegenerate,
    isBed = false,
    regenerating,
    onRetexture,
    hasOwnModel,
    modelSourceOptions = [],
    retexturing,
    busy,
}) => {
    const saved = variant.meshyConfig || {};
    const savedModel = saved.model || {};
    const savedTexture = saved.texture || {};
    const savedUrls = (savedModel.imageUrls || []).filter(Boolean);
    const cutoutUrl = variant.imageId ? catalogImageUrl(variant.imageId, 512) : null;

    // Model config
    const [modelPbr, setModelPbr] = useState(pbrModeOf(savedModel.enablePbr));
    const [modelRemoveLighting, setModelRemoveLighting] = useState(savedModel.removeLighting === true);
    const [modelPolycount, setModelPolycount] = useState(savedModel.targetPolycount ?? null);
    const [multiImage, setMultiImage] = useState(savedUrls.length > 0);
    const [selected, setSelected] = useState(savedUrls);
    // Beds: the bedding-free edits of the source images are cached per source URL;
    // a bad edit is redone only on request (each edit is a paid image-model call).
    const [redoBeddingRemoval, setRedoBeddingRemoval] = useState(false);
    const hasFrameImages = Object.keys(savedModel.bedFrameImages || {}).length > 0;
    // One-shot: the redo applies to the regenerate it was armed for.
    useEffect(() => {
        if (regenerating) setRedoBeddingRemoval(false);
    }, [regenerating]);

    // Texture (retexture) config
    const [texturePbr, setTexturePbr] = useState(pbrModeOf(savedTexture.enablePbr));
    const [textureRemoveLighting, setTextureRemoveLighting] = useState(savedTexture.removeLighting === true);
    const [styleUrl, setStyleUrl] = useState(savedTexture.imageStyleUrl || '');
    const [stylePrompt, setStylePrompt] = useState(savedTexture.textStylePrompt || '');
    // Cross-variant retexture: use another variant's model as the geometry input.
    const [useSourceVariant, setUseSourceVariant] = useState(!!savedTexture.sourceVariantId);
    const [sourceVariantId, setSourceVariantId] = useState(savedTexture.sourceVariantId || null);
    // Uploaded reference images kept in the picker. Seeded with the saved model
    // images too: an upload from an earlier session is still in `selected` but is
    // no candidate, so without a thumb it can't be deselected and silently fills
    // the MAX_MODEL_IMAGES cap.
    const [uploadedUrls, setUploadedUrls] = useState(() => [
        ...new Set([savedTexture.imageStyleUrl, ...savedUrls].filter(Boolean)),
    ]);
    const [uploadingStyle, setUploadingStyle] = useState(false);
    const styleUploadRef = useRef(null);
    const modelUploadRef = useRef(null);

    // Shared candidate gallery (loaded on demand for either picker)
    const [candidates, setCandidates] = useState([]);
    const [loadingImages, setLoadingImages] = useState(false);
    const [showStylePicker, setShowStylePicker] = useState(false);

    useEffect(() => {
        onModelChange({
            enablePbr: enablePbrOf(modelPbr),
            removeLighting: modelRemoveLighting,
            targetPolycount: modelPolycount ?? undefined,
            imageUrls: multiImage ? selected : [],
            ...(isBed && redoBeddingRemoval ? { redoBeddingRemoval: true } : {}),
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modelPbr, modelRemoveLighting, modelPolycount, multiImage, selected, redoBeddingRemoval, isBed]);

    const loadCandidates = async () => {
        if (candidates.length) return;
        setLoadingImages(true);
        try {
            const { candidates: urls } = await ingestionApi.getVariantImages(itemId, variant.id);
            setCandidates(urls || []);
        } catch (e) {
            message.error(e.message || 'Failed to load images');
        } finally {
            setLoadingImages(false);
        }
    };

    // A saved selection opens the picker on mount; its thumbnails must be loaded
    // too, or only the cutout shows while the hidden picks still count to the cap.
    useEffect(() => {
        if (multiImage) loadCandidates();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onToggleMulti = (on) => {
        setMultiImage(on);
        if (on) loadCandidates();
    };

    const onToggleStylePicker = (on) => {
        setShowStylePicker(on);
        if (on) loadCandidates();
        if (!on) setStyleUrl('');
    };

    const onToggleSourceVariant = (on) => {
        setUseSourceVariant(on);
        if (!on) setSourceVariantId(null);
    };
    const selectedSource = useSourceVariant ? sourceVariantId : null;
    const canRetexture = hasOwnModel || !!selectedSource;

    const MAX_MODEL_IMAGES = 3;
    const toggleImage = (url) =>
        setSelected((prev) => {
            if (prev.includes(url)) return prev.filter((u) => u !== url);
            if (prev.length >= MAX_MODEL_IMAGES) {
                message.info(`Up to ${MAX_MODEL_IMAGES} images`);
                return prev;
            }
            return [...prev, url];
        });

    // Both uploads publish a reference image to the image CDN (never stored as the
    // variant image) — they differ only in what gets selected with the result.
    const uploadReferenceImage = async (file, onUploaded) => {
        setUploadingStyle(true);
        try {
            const { url } = await ingestionApi.uploadRetextureStyleImage(itemId, variant.id, file);
            setUploadedUrls((prev) => (prev.includes(url) ? prev : [...prev, url]));
            onUploaded(url);
            message.success('Image uploaded');
        } catch (e) {
            message.error(e.message || 'Failed to upload image');
        } finally {
            setUploadingStyle(false);
        }
    };
    const uploadStyleImage = (file) => uploadReferenceImage(file, setStyleUrl);
    const uploadModelImage = (file) =>
        uploadReferenceImage(file, (url) =>
            setSelected((prev) => (prev.includes(url) || prev.length >= MAX_MODEL_IMAGES ? prev : [...prev, url])),
        );

    // Uploaded refs (this session's, or saved picks from an earlier upload) that
    // aren't already shown as cutout/candidate thumbs — every pick stays deselectable.
    const uploadedThumbs = [...new Set([...uploadedUrls, ...selected])].filter(
        (u) => u !== cutoutUrl && !candidates.includes(u),
    );

    const modelPanel = (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <SettingsToggles
                pbr={modelPbr}
                setPbr={setModelPbr}
                autoPbr={saved.autoPbr}
                removeLighting={modelRemoveLighting}
                setRemoveLighting={setModelRemoveLighting}
                polycount={modelPolycount}
                setPolycount={setModelPolycount}
            />
            <span>
                <Switch checked={multiImage} onChange={onToggleMulti} />{' '}
                <Text>Use different image / images</Text>
            </span>
            {isBed && (
                <span>
                    <Switch checked={redoBeddingRemoval} onChange={setRedoBeddingRemoval} disabled={!hasFrameImages} />{' '}
                    <Text>Redo the bedding removal</Text>{' '}
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        {hasFrameImages
                            ? '— edits the source photo again instead of reusing the cached bare-frame image'
                            : '— no bare-frame image yet; the first run produces it'}
                    </Text>
                </span>
            )}
            {multiImage && (
                <div>
                    <Text type="secondary">
                        Pick 1 image to generate from it instead of the cutout, or 2–{MAX_MODEL_IMAGES} product angles
                        for multi-image generation.
                    </Text>
                    {loadingImages ? (
                        <div style={{ padding: 16, textAlign: 'center' }}>
                            <Spin />
                        </div>
                    ) : !cutoutUrl && candidates.length === 0 && uploadedThumbs.length === 0 ? (
                        <Alert style={{ marginTop: 8 }} type="info" showIcon message="No candidate images for this variant." />
                    ) : (
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                            {cutoutUrl && (
                                <PickThumb
                                    url={cutoutUrl}
                                    on={selected.includes(cutoutUrl)}
                                    onClick={() => toggleImage(cutoutUrl)}
                                    badge="cutout"
                                    testId={`meshy-img-cutout-${variant.id}`}
                                />
                            )}
                            {candidates.map((url) => (
                                <PickThumb
                                    key={url}
                                    url={url}
                                    on={selected.includes(url)}
                                    onClick={() => toggleImage(url)}
                                    testId={`meshy-img-${variant.id}`}
                                />
                            ))}
                            {uploadedThumbs.map((url) => (
                                <PickThumb
                                    key={url}
                                    url={url}
                                    on={selected.includes(url)}
                                    onClick={() => toggleImage(url)}
                                    badge="uploaded"
                                    testId={`meshy-img-uploaded-${variant.id}`}
                                />
                            ))}
                        </div>
                    )}
                    {selected.length === 0 && (
                        <Alert
                            style={{ marginTop: 8 }}
                            type="info"
                            showIcon
                            message="No image selected — the cutout will be used."
                        />
                    )}
                    <input
                        ref={modelUploadRef}
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) uploadModelImage(file);
                            e.target.value = '';
                        }}
                    />
                    <Button
                        size="small"
                        icon={<UploadOutlined />}
                        loading={uploadingStyle}
                        style={{ marginTop: 8 }}
                        onClick={() => modelUploadRef.current?.click()}
                    >
                        Upload image
                    </Button>
                </div>
            )}
            <Button
                icon={<RedoOutlined />}
                loading={regenerating}
                disabled={busy}
                onClick={onRegenerate}
                style={{ alignSelf: 'flex-start' }}
            >
                Regenerate model
            </Button>
        </div>
    );

    const texturePanel = (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <SettingsToggles
                pbr={texturePbr}
                setPbr={setTexturePbr}
                autoPbr={saved.autoPbr}
                removeLighting={textureRemoveLighting}
                setRemoveLighting={setTextureRemoveLighting}
            />
            <div>
                <Text strong>Texture style prompt</Text>
                <Input.TextArea
                    value={stylePrompt}
                    onChange={(e) => setStylePrompt(e.target.value)}
                    maxLength={600}
                    showCount
                    autoSize={{ minRows: 2, maxRows: 6 }}
                    placeholder="Describe the texture style (e.g. 'weathered light oak wood, matte finish'). When filled, this replaces the style image."
                    style={{ marginTop: 4 }}
                />
            </div>
            <span>
                <Switch
                    checked={showStylePicker}
                    onChange={onToggleStylePicker}
                    disabled={!!stylePrompt.trim()}
                />{' '}
                <Text type={stylePrompt.trim() ? 'secondary' : undefined}>
                    Choose a different image for retexturing{stylePrompt.trim() ? ' (ignored — text prompt is set)' : ''}
                </Text>
            </span>
            {showStylePicker && (
                <div>
                    <Text type="secondary">Pick the image to retexture from (default: the cutout).</Text>
                    {loadingImages ? (
                        <div style={{ padding: 16, textAlign: 'center' }}>
                            <Spin />
                        </div>
                    ) : (
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                            {cutoutUrl && (
                                <PickThumb
                                    url={cutoutUrl}
                                    on={styleUrl === ''}
                                    onClick={() => setStyleUrl('')}
                                    badge="cutout"
                                    testId={`meshy-style-cutout-${variant.id}`}
                                />
                            )}
                            {candidates.map((url) => (
                                <PickThumb
                                    key={url}
                                    url={url}
                                    on={styleUrl === url}
                                    onClick={() => setStyleUrl(url)}
                                    testId={`meshy-style-img-${variant.id}`}
                                />
                            ))}
                            {uploadedThumbs.map((url) => (
                                <PickThumb
                                    key={url}
                                    url={url}
                                    on={styleUrl === url}
                                    onClick={() => setStyleUrl(url)}
                                    badge="uploaded"
                                    testId={`meshy-style-uploaded-${variant.id}`}
                                />
                            ))}
                        </div>
                    )}
                    <input
                        ref={styleUploadRef}
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) uploadStyleImage(file);
                            e.target.value = '';
                        }}
                    />
                    <Button
                        size="small"
                        icon={<UploadOutlined />}
                        loading={uploadingStyle}
                        style={{ marginTop: 8 }}
                        onClick={() => styleUploadRef.current?.click()}
                    >
                        Upload style image
                    </Button>
                </div>
            )}
            <span>
                <Switch checked={useSourceVariant} onChange={onToggleSourceVariant} />{' '}
                <Text>Retexture a model of a different variant</Text>
            </span>
            {useSourceVariant &&
                (modelSourceOptions.length === 0 ? (
                    <Alert type="info" showIcon message="No other variant has a model yet." />
                ) : (
                    <div>
                        <Text type="secondary">
                            Pick the variant whose model to retexture — the result becomes this variant’s model.
                        </Text>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                            {modelSourceOptions.map((opt) =>
                                opt.cutoutUrl ? (
                                    <PickThumb
                                        key={opt.variantId}
                                        url={opt.cutoutUrl}
                                        on={sourceVariantId === opt.variantId}
                                        onClick={() => setSourceVariantId(opt.variantId)}
                                        badge={opt.label}
                                    />
                                ) : (
                                    // No cutout to show — a label-only tile keeps the variant pickable.
                                    <div
                                        key={opt.variantId}
                                        onClick={() => setSourceVariantId(opt.variantId)}
                                        style={{
                                            ...thumb(sourceVariantId === opt.variantId),
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: 11,
                                            textAlign: 'center',
                                            background: '#f0f0f0',
                                            overflow: 'hidden',
                                        }}
                                    >
                                        {opt.label}
                                    </div>
                                ),
                            )}
                        </div>
                    </div>
                ))}
            <Tooltip title={canRetexture ? '' : 'Generate or upload a model first, or pick another variant’s model'}>
                <Button
                    icon={<BgColorsOutlined />}
                    disabled={!canRetexture || busy}
                    loading={retexturing}
                    style={{ alignSelf: 'flex-start' }}
                    onClick={() =>
                        onRetexture({
                            enablePbr: enablePbrOf(texturePbr),
                            removeLighting: textureRemoveLighting,
                            imageStyleUrl: styleUrl || undefined,
                            textStylePrompt: stylePrompt.trim() || undefined,
                            sourceVariantId: selectedSource || undefined,
                        })
                    }
                >
                    Retexture
                </Button>
            </Tooltip>
        </div>
    );

    return (
        <Collapse
            size="small"
            items={[
                { key: 'model', label: 'Regenerate model', children: modelPanel },
                { key: 'texture', label: 'Retexture', children: texturePanel },
            ]}
        />
    );
};
